import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import moment from "moment";
import Hls from "hls.js";
import WhiteTitledCard from "../../../components/cards/WhiteTitledCard";
import { useAppSelector, useAppDispatch } from "../../../redux/app/hooks";
import { RootState } from "../../../redux/app/store";
import {
  approveProductAsync,
  fetchProductsDetailsAsync,
} from "../../../redux/features/ProductSlice";
import { Loader2 } from "lucide-react";
import { toast } from "react-toastify";
import EditProductModal from "./UpdateProductDetails";

// STATUS TAG COMPONENT
const StatusTag = ({ status }: { status?: string }) => {
  const statusColors: Record<string, string> = {
    PENDING: "bg-yellow-100 text-yellow-700 border-yellow-200",
    APPROVED: "bg-green-100 text-green-700 border-green-200",
    REJECTED: "bg-red-100 text-red-700 border-red-200",
    ACTIVE: "bg-blue-100 text-blue-700 border-blue-200",
    INACTIVE: "bg-gray-100 text-gray-700 border-gray-200",
  };

  const classes =
    statusColors[status ?? ""] ?? "bg-gray-100 text-gray-700 border-gray-200";

  return (
    <span
      className={`px-3 py-1 rounded-full text-xs font-semibold border ${classes}`}
    >
      {status
        ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
        : "—"}
    </span>
  );
};

// DETAIL COMPONENT
const Detail = ({ label, value }: { label?: string; value?: any }) => (
  <div>
    <span className="text-gray-500 text-sm">{label}:</span>
    <p className="font-medium text-gray-800 mt-1">{value ?? "—"}</p>
  </div>
);

// UNIFIED VIDEO PLAYER (HLS.js)
const VideoPlayer = ({ hlsUrl }: { hlsUrl: string }) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    let hls: Hls | null = null;

    if (Hls.isSupported()) {
      hls = new Hls({ enableWorker: true, startLevel: -1 });
      hls.loadSource(hlsUrl);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        video.play().catch(() => {});
      });
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      // Safari native HLS
      video.src = hlsUrl;
      video.addEventListener("loadedmetadata", () => {
        video.play().catch(() => {});
      });
    }

    return () => {
      if (hls) {
        hls.destroy();
      }
    };
  }, [hlsUrl]);

  return (
    <div
      className="w-full rounded-md overflow-hidden bg-black"
      style={{ aspectRatio: "16/9" }}
    >
      <video
        ref={videoRef}
        className="w-full h-full"
        controls
        controlsList="nodownload"
        playsInline
      />
    </div>
  );
};

// MAIN COMPONENT
const ViewProductDetails: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [rejectionReasonInput, setRejectionReasonInput] = useState("");
  const [openModal, setOpenModal] = useState<{
    type: "approve" | "reject" | null;
  }>({
    type: null,
  });

  const [mediaIndex, setMediaIndex] = useState(0);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);

  const { loading, productDetails } = useAppSelector(
    (state: RootState) => state.products,
  );

  const { id: prodId } = useParams();

  // FETCH PRODUCT DETAILS
  useEffect(() => {
    dispatch(fetchProductsDetailsAsync(prodId));
  }, [prodId, dispatch]);

  // LOADING VIEW
  if (loading && !productDetails) {
    return (
      <div className="p-6 flex justify-center items-center min-h-screen">
        <Loader2 className="w-10 h-10 animate-spin text-gray-500" />
      </div>
    );
  }

  // NOT FOUND VIEW
  if (!productDetails) {
    return (
      <div className="p-6">
        <button
          className="text-sm text-gray-600 mb-4"
          onClick={() => navigate(-1)}
        >
          &larr; Back
        </button>

        <WhiteTitledCard>
          <div className="p-6">
            <p className="text-gray-700">Product not found</p>
          </div>
        </WhiteTitledCard>
      </div>
    );
  }

  // PRODUCT DETAILS
  const {
    name,
    description,
    photos = [],
    videoUploads = [],
    price,
    quantity,
    colors,
    sizes,
    tags,
    soldQuantity,
    approvalStatus,
    approvedBy,
    approvedAt,
    rejectionReason,
    status,
    currencyId,
    categoryIds,
    vendorId,
    createdAt,
    updatedAt,
    id: productId,
  } = productDetails;

  // HANDLE APPROVAL / REJECTION
  const handleConfirmApprove = async () => {
    const newStatus = openModal.type === "approve" ? "APPROVED" : "REJECTED";

    if (newStatus === "REJECTED" && !rejectionReasonInput.trim()) {
      toast.error("Rejection reason is required");
      return;
    }

    await dispatch(
      approveProductAsync({
        productId,
        approvalStatus: newStatus,
        rejectionReason:
          newStatus === "REJECTED" ? rejectionReasonInput.trim() : undefined,
      }),
    ).unwrap();

    toast.success(
      `Product ${newStatus.toLowerCase()} successfully${
        newStatus === "REJECTED" ? ` with reason: ${rejectionReasonInput}` : ""
      }`,
    );

    setOpenModal({ type: null });
    setRejectionReasonInput("");
  };

  // MAIN UI RENDER
  return (
    <div className="p-6 sm:p-8 bg-gray-50 min-h-screen">
      {/* BACK BUTTON */}
      <button
        className="text-gray-600 mb-4 inline-flex items-center gap-2"
        onClick={() => navigate(-1)}
      >
        &larr; Back
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* LEFT COLUMN IMAGES & DESCRIPTION */}
        <div className="lg:col-span-2 gap-6 flex flex-col">
          {/*  Media Gallery */}
          <WhiteTitledCard>
            <div>
              {/* Main display area */}
              {(() => {
                const isVideo = mediaIndex >= photos.length;
                const videoIdx = mediaIndex - photos.length;

                if (isVideo && videoUploads[videoIdx]) {
                  const video = videoUploads[videoIdx];

                  // Resolve thumbnail
                  const thumbUrl =
                    video?.metadata?.playData?.thumbnailUrl ??
                    video?.metadata?.playData?.previewUrl ??
                    video?.metadata?.thumbnail ??
                    video?.thumbnailUrl;

                  // Resolve HLS stream URL — support BunnyCDN & Cloudflare
                  const hlsUrl =
                    video?.metadata?.playData?.videoPlaylistUrl ?? // BunnyCDN
                    video?.metadata?.playback?.hls ?? // Cloudflare
                    null;

                  if (!hlsUrl) {
                    return (
                      <div className="w-full h-80 rounded-md bg-gray-100 flex items-center justify-center text-gray-400">
                        Video not available
                      </div>
                    );
                  }

                  // Click-to-play: show thumbnail first, then native player
                  return videoPlaying ? (
                    <VideoPlayer hlsUrl={hlsUrl} />
                  ) : (
                    <div
                      className="w-full rounded-md overflow-hidden bg-black cursor-pointer relative group"
                      style={{ aspectRatio: "16/9" }}
                      onClick={() => setVideoPlaying(true)}
                    >
                      {thumbUrl ? (
                        <img
                          src={thumbUrl}
                          alt={video?.name ?? "Video thumbnail"}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gray-800" />
                      )}
                      {/* Big play button */}
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors">
                        <div className="w-16 h-16 rounded-full bg-white/90 group-hover:bg-white group-hover:scale-110 transition-all flex items-center justify-center shadow-lg">
                          <svg
                            className="w-8 h-8 text-gray-800 ml-1"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  );
                }

                // Image display
                return (
                  <div
                    className="w-full h-80 rounded-md overflow-hidden bg-gray-100 flex items-center justify-center cursor-zoom-in group relative"
                    onClick={() => photos.length > 0 && setLightboxOpen(true)}
                  >
                    {photos.length > 0 ? (
                      <>
                        <img
                          src={photos[mediaIndex]}
                          alt={`${name} ${mediaIndex}`}
                          className="object-contain w-full h-full"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                          <svg
                            className="w-8 h-8 text-white opacity-0 group-hover:opacity-80 transition-opacity drop-shadow-lg"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            viewBox="0 0 24 24"
                          >
                            <path d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16zM11 8v6M8 11h6" />
                          </svg>
                        </div>
                      </>
                    ) : (
                      <div className="text-gray-400">No image</div>
                    )}
                  </div>
                );
              })()}

              {/* Thumbnails */}
              {photos.length + videoUploads.length > 1 && (
                <div className="my-3 grid grid-cols-4 gap-2">
                  {/* Image thumbnails */}
                  {photos.map((p: any, i: number) => (
                    <img
                      key={p + i}
                      src={p}
                      alt={`${name} thumb ${i}`}
                      onClick={() => {
                        setMediaIndex(i);
                        setVideoPlaying(false);
                      }}
                      className={`h-20 w-full object-cover rounded cursor-pointer border transition-all duration-200 hover:scale-105 hover:shadow-lg hover:ring-2 hover:ring-indigo-300 ${
                        mediaIndex === i
                          ? "ring-2 ring-indigo-400"
                          : "border-gray-200"
                      }`}
                    />
                  ))}

                  {/* Video thumbnails */}
                  {videoUploads.map((video: any, idx: number) => {
                    const thumbUrl =
                      video?.metadata?.playData?.thumbnailUrl ??
                      video?.metadata?.playData?.previewUrl ??
                      video?.metadata?.thumbnail ??
                      video?.thumbnailUrl;
                    const globalIdx = photos.length + idx;
                    return (
                      <div
                        key={video.id ?? idx}
                        onClick={() => {
                          setMediaIndex(globalIdx);
                          setVideoPlaying(false);
                        }}
                        className={`relative h-20 rounded cursor-pointer border-2 overflow-hidden bg-gray-900 flex items-center justify-center transition-all duration-200 hover:scale-105 hover:shadow-lg hover:ring-2 hover:ring-indigo-300 ${
                          mediaIndex === globalIdx
                            ? "ring-2 ring-indigo-400 border-indigo-400"
                            : "border-gray-200"
                        }`}
                      >
                        {thumbUrl ? (
                          <img
                            src={thumbUrl}
                            alt={video?.name ?? `Video ${idx + 1}`}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-gray-700 flex items-center justify-center">
                            <svg
                              className="w-8 h-8 text-gray-400"
                              fill="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z" />
                            </svg>
                          </div>
                        )}
                        {/* Play icon overlay */}
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                          <svg
                            className="w-6 h-6 text-white drop-shadow"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </WhiteTitledCard>

          {/* Description */}
          <WhiteTitledCard>
            <h3 className="text-lg font-semibold mb-2">Description</h3>
            <div className="text-sm text-gray-700 whitespace-pre-wrap">
              {description ?? "—"}
            </div>
          </WhiteTitledCard>
        </div>

        {/* RIGHT COLUMN (DETAILS) */}
        <div className="lg:col-span-3 space-y-6">
          {/* General Info */}
          <WhiteTitledCard>
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold text-gray-800">{name}</h1>
                  {videoUploads.length > 0 && (
                    <span className="inline-flex items-center gap-1 bg-indigo-100 text-indigo-700 text-xs font-semibold px-2 py-1 rounded-full">
                      <svg
                        className="w-3.5 h-3.5"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z" />
                      </svg>
                      {videoUploads.length} video
                      {videoUploads.length > 1 ? "s" : ""}
                    </span>
                  )}
                </div>

                <div className="gap-3 mt-2">
                  <p className="text-sm font-semibold text-gray-700">
                    Category:
                  </p>

                  <div className="flex gap-3">
                    <span className="py-1 text-sm text-gray-600">
                      {categoryIds?.title ?? "Uncategorized"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex flex-col items-end gap-2">
                <StatusTag status={approvalStatus ?? status} />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
              <Detail
                label="Price"
                value={`${currencyId?.symbol ?? ""} ${price ?? "—"}`}
              />
              <Detail label="Quantity" value={quantity ?? "—"} />
              <Detail label="Sold" value={soldQuantity ?? 0} />
            </div>
          </WhiteTitledCard>

          {/* Attributes & Vendor */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Attributes */}
            <WhiteTitledCard>
              <h3 className="text-lg font-semibold mb-3">Attributes</h3>

              <div className="space-y-3">
                <Detail
                  label="Colors"
                  value={
                    Array.isArray(colors)
                      ? colors.length
                        ? colors.join(", ")
                        : "—"
                      : (colors ?? "—")
                  }
                />

                <div>
                  <span className="text-gray-500 text-sm">Size Details:</span>

                  {sizes?.map((size: any) => (
                    <p className="font-medium text-gray-800 mt-1">
                      {size?.sizeVariant
                        ? `${size.sizeVariant.label} — ${Object.entries(
                            size.sizeVariant.measurements,
                          )
                            .map(([k, v]) => `${k}: ${v}`)
                            .join(", ")} — Stock: ${size.stock} — Price: ${
                            size.sizePrice
                          }`
                        : "—"}
                    </p>
                  ))}
                </div>

                <Detail
                  label="Tags"
                  value={
                    Array.isArray(tags)
                      ? tags.length
                        ? tags.join(", ")
                        : "—"
                      : (tags ?? "—")
                  }
                />
              </div>
            </WhiteTitledCard>

            {/* Vendor */}
            <WhiteTitledCard>
              <h3 className="text-lg font-semibold mb-3">Vendor</h3>

              <div className="space-y-3">
                <Detail
                  label="Vendor Name"
                  value={
                    vendorId?.fullName ??
                    `${vendorId?.firstName ?? ""} ${vendorId?.lastName ?? ""}`
                  }
                />
                <Detail label="Vendor Email" value={vendorId?.email ?? "—"} />
                <Detail
                  label="Vendor Phone"
                  value={vendorId?.phoneNumber ?? "—"}
                />
              </div>
            </WhiteTitledCard>
          </div>

          {/* Approval Section */}
          <WhiteTitledCard>
            <h3 className="text-lg font-semibold mb-3">Approval & Status</h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Detail label="Approval Status" value={approvalStatus ?? "—"} />
              <Detail
                label="Approved By"
                value={approvedBy?.fullName ?? approvedBy ?? "—"}
              />
              <Detail
                label="Approved At"
                value={
                  approvedAt
                    ? moment(approvedAt).format("Do MMM YYYY, h:mm A")
                    : "—"
                }
              />
              <Detail label="Rejection Reason" value={rejectionReason ?? "—"} />
              <Detail label="Status" value={status ?? "—"} />
            </div>
          </WhiteTitledCard>

          {/* Meta Info */}
          <WhiteTitledCard>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Detail
                label="Created At"
                value={
                  createdAt
                    ? moment(createdAt).format("Do MMM YYYY, h:mm A")
                    : "—"
                }
              />
              <Detail
                label="Updated At"
                value={
                  updatedAt
                    ? moment(updatedAt).format("Do MMM YYYY, h:mm A")
                    : "—"
                }
              />
              <Detail
                label="Currency"
                value={currencyId?.name ?? currencyId?.symbol ?? "—"}
              />
            </div>
          </WhiteTitledCard>

          {/* ACTION BUTTONS */}
          <div className="flex justify-end gap-4">
            <button
              className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
              onClick={() => setOpenEditModal(true)}
            >
              Edit Product
            </button>

            {(approvalStatus === "PENDING" ||
              approvalStatus === "APPROVED") && (
              <button
                className="px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 disabled:opacity-50 flex items-center gap-2"
                onClick={() => setOpenModal({ type: "reject" })}
                disabled={loading}
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  "Reject"
                )}
              </button>
            )}

            {(approvalStatus === "PENDING" ||
              approvalStatus === "REJECTED") && (
              <button
                className="px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                onClick={() => setOpenModal({ type: "approve" })}
                disabled={loading}
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  "Approve"
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* APPROVE / REJECT MODAL */}
      {openModal.type && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-lg p-6">
            <h2 className="text-lg font-semibold mb-3 capitalize">
              {openModal.type} Product
            </h2>

            <p className="text-sm text-gray-700">
              Are you sure you want to{" "}
              <span className="font-semibold">{openModal.type}</span>{" "}
              <span className="font-semibold">{name}</span>?
            </p>

            {openModal.type === "reject" && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rejection Reason <span className="text-red-500">*</span>
                </label>

                <textarea
                  className="w-full border rounded-md p-2 text-sm focus:ring focus:ring-red-300"
                  rows={3}
                  value={rejectionReasonInput}
                  onChange={(e) => setRejectionReasonInput(e.target.value)}
                  disabled={loading}
                  placeholder="Enter reason for rejection"
                />
              </div>
            )}

            <div className="flex gap-3 justify-end mt-6">
              <button
                className="px-4 py-2 rounded border border-gray-300"
                onClick={() => setOpenModal({ type: null })}
                disabled={loading}
              >
                Cancel
              </button>

              <button
                className={`px-4 py-2 rounded text-white flex items-center gap-2 ${
                  openModal.type === "approve"
                    ? "bg-green-600 hover:bg-green-700"
                    : "bg-red-500 hover:bg-red-600"
                } disabled:opacity-50`}
                onClick={handleConfirmApprove}
                disabled={loading}
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  `Confirm ${openModal.type}`
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <EditProductModal
        open={openEditModal}
        onClose={() => {
          setOpenEditModal(false);
        }}
        product={productDetails}
        onSave={() => {
          dispatch(fetchProductsDetailsAsync(prodId));
        }}
      />

      {/* FULLSCREEN IMAGE LIGHTBOX */}
      {lightboxOpen && photos.length > 0 && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90"
          onClick={() => setLightboxOpen(false)}
        >
          {/* Close button */}
          <button
            className="absolute top-4 right-4 text-white/80 hover:text-white z-10 bg-black/40 hover:bg-black/60 rounded-full p-2 transition-colors"
            onClick={() => setLightboxOpen(false)}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Image counter */}
          {photos.length > 1 && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 text-white/80 text-sm bg-black/40 px-3 py-1 rounded-full">
              {Math.min(mediaIndex, photos.length - 1) + 1} / {photos.length}
            </div>
          )}

          {/* Previous button */}
          {photos.length > 1 && (
            <button
              className="absolute left-4 text-white/80 hover:text-white bg-black/40 hover:bg-black/60 rounded-full p-3 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                setMediaIndex((prev) => {
                  const cur = Math.min(prev, photos.length - 1);
                  return (cur - 1 + photos.length) % photos.length;
                });
              }}
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                viewBox="0 0 24 24"
              >
                <path d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}

          {/* Full-size image */}
          <img
            src={photos[Math.min(mediaIndex, photos.length - 1)]}
            alt={`${name} full ${mediaIndex}`}
            className="max-h-[90vh] max-w-[90vw] object-contain select-none"
            onClick={(e) => e.stopPropagation()}
          />

          {/* Next button */}
          {photos.length > 1 && (
            <button
              className="absolute right-4 text-white/80 hover:text-white bg-black/40 hover:bg-black/60 rounded-full p-3 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                setMediaIndex((prev) => {
                  const cur = Math.min(prev, photos.length - 1);
                  return (cur + 1) % photos.length;
                });
              }}
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                viewBox="0 0 24 24"
              >
                <path d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}

          {/* Thumbnail strip at bottom */}
          {photos.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
              {photos.map((p: string, i: number) => (
                <img
                  key={p + i}
                  src={p}
                  alt={`thumb ${i}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setMediaIndex(i);
                  }}
                  className={`h-14 w-14 object-cover rounded cursor-pointer border-2 transition-all ${
                    Math.min(mediaIndex, photos.length - 1) === i
                      ? "border-white ring-2 ring-white/50 scale-110"
                      : "border-transparent opacity-60 hover:opacity-100"
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ViewProductDetails;
